import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { getTz, fmtTs } from '../lib/globalTz';
import {
  createReportPdf, drawSectionBanner, drawTable, drawKpiTiles, drawEmptyState,
  drawClosingFooter, finalizeFooters, fmtGeneratedAt, savePdf, riskCell,
  preloadPdfLogo, ensureSpace, MARGIN_X, CONTENT_W, type TableCell, type KpiTile, type DocCtx,
} from '../lib/pdfTemplate';

type Lookback = '24h' | '7d' | '30d' | '60d' | '90d';
type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';

interface RiskTile {
  customerId: string;
  customerName: string;
  riskIndex: number;
  riskLevel: RiskLevel;
  reason: string;
  currentIncidentCount: number;
  criticalUnresolvedCount: number;
  exposureScore: number | null;
  secureScore: number | null;
  edrHealth: string;
  connectorHealth: string;
  tiHitCount: number;
  slaBreachRisk: number;
  riskyUsers: number;
  riskyDevices: number;
  lastSuccessfulLogIngestion: string | null;
  diagnostics: string[];
  signals: Record<string, unknown>;
}

interface RiskPayload {
  ok: true;
  lookback: Lookback;
  generatedAt: string;
  cacheHit?: boolean;
  totals: { customers: number; critical: number; high: number; medium: number; low: number; totalIncidents: number; criticalUnresolved: number; tiHits: number };
  tiles: RiskTile[];
  focus: Array<{ customerId: string; customerName: string; riskIndex: number; riskLevel: RiskLevel; reason: string }>;
  diagnostics: string[];
}

const LOOKBACKS: Lookback[] = ['24h', '7d', '30d', '60d', '90d'];

function tone(value: string | number) {
  const v = String(value).toLowerCase();
  const n = typeof value === 'number' ? value : Number.NaN;
  if (v.includes('critical') || v.includes('degraded') || (!Number.isNaN(n) && n >= 85)) return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-200';
  if (v.includes('high') || v.includes('warning') || (!Number.isNaN(n) && n >= 65)) return 'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-200';
  if (v.includes('medium') || v.includes('unknown') || (!Number.isNaN(n) && n >= 35)) return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-200';
  return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
}
function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
/** Long-form narrative body text (not tabular) - paragraphs with page-break
 *  handling, drawn between the branded header and footer. */
function addParagraphs(ctx: DocCtx, text: string, size = 9.5) {
  const { pdf } = ctx;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(size);
  for (const block of text.split(/\n{2,}/)) {
    const lines = pdf.splitTextToSize(block || ' ', CONTENT_W);
    for (const line of lines) {
      ensureSpace(ctx, 6);
      pdf.text(String(line), MARGIN_X, ctx.y);
      ctx.y += 5;
    }
    ctx.y += 3;
  }
}

async function exportPdf(payload: RiskPayload, aiAnalysis: string | null) {
  await preloadPdfLogo();
  const ctx = createReportPdf({
    title: 'Risk Command Center',
    customerName: 'All Customers (MSSP)',
    periodLabel: `Lookback ${payload.lookback}`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, getTz()),
  });

  const kpis: KpiTile[] = [
    { value: String(payload.totals.customers), label: 'Customers' },
    { value: String(payload.totals.critical), label: 'Critical', tone: payload.totals.critical > 0 ? 'danger' : 'default' },
    { value: String(payload.totals.high), label: 'High', tone: payload.totals.high > 0 ? 'warning' : 'default' },
    { value: String(payload.totals.medium), label: 'Medium' },
    { value: String(payload.totals.low), label: 'Low', tone: 'success' },
    { value: String(payload.totals.totalIncidents), label: 'Open Incidents' },
    { value: String(payload.totals.criticalUnresolved), label: 'Critical Unresolved', tone: payload.totals.criticalUnresolved > 0 ? 'danger' : 'default' },
    { value: String(payload.totals.tiHits), label: 'TI Hits' },
  ];
  drawKpiTiles(ctx, kpis);

  if (aiAnalysis) {
    drawSectionBanner(ctx, 'AI Analysis');
    addParagraphs(ctx, aiAnalysis.slice(0, 3500));
  }

  drawSectionBanner(ctx, 'Focus Today');
  if (payload.focus.length === 0) {
    drawEmptyState(ctx, 'No customers flagged for focus today.');
  } else {
    const rows: TableCell[][] = payload.focus.slice(0, 8).map((f) => [
      f.customerName,
      { content: String(f.riskIndex), styles: { halign: 'center', fontStyle: 'bold' } },
      riskCell(f.riskLevel),
      f.reason,
    ]);
    drawTable(ctx, {
      head: ['Customer', 'Risk Index', 'Risk Level', 'Reason'],
      body: rows,
      columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 24, halign: 'center' }, 3: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  drawSectionBanner(ctx, 'Customer Risk Tiles');
  if (payload.tiles.length === 0) {
    drawEmptyState(ctx, 'No customer risk tiles available.');
  } else {
    const rows: TableCell[][] = payload.tiles.slice(0, 30).map((t) => [
      t.customerName,
      { content: String(t.riskIndex), styles: { halign: 'center', fontStyle: 'bold' } },
      riskCell(t.riskLevel),
      { content: `${t.currentIncidentCount}/${t.criticalUnresolvedCount}`, styles: { halign: 'center' } },
      `Exposure ${t.exposureScore ?? '-'} · Secure ${t.secureScore ?? '-'} · EDR ${t.edrHealth} · Connector ${t.connectorHealth} · TI ${t.tiHitCount} · SLA ${t.slaBreachRisk} · Risky ${t.riskyUsers}/${t.riskyDevices}`,
      t.reason,
    ]);
    drawTable(ctx, {
      head: ['Customer', 'Risk Index', 'Risk Level', 'Incidents/Crit', 'Signals', 'Reason'],
      body: rows,
      columnStyles: {
        0: { cellWidth: 26 },
        1: { cellWidth: 16, halign: 'center' },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 60 },
        5: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  return savePdf(ctx, `risk-command-center-${payload.lookback}`);
}

function Metric({ label, value, helper }: { label: string; value: string | number; helper?: string }) {
  return <div className="rounded-2xl border bg-card p-4"><div className="text-xs text-muted-foreground">{label}</div><div className="text-2xl font-bold">{value}</div>{helper && <div className="text-xs text-muted-foreground mt-1">{helper}</div>}</div>;
}

export function RiskCommandCenterTab() {
  const { canCapability } = useAuth();
  const { setActiveCustomerId } = useCustomers();
  const [lookback, setLookback] = useState<Lookback>('24h');
  const [payload, setPayload] = useState<RiskPayload | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [selected, setSelected] = useState<RiskTile | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canRefresh = canCapability('risk_command_center', 'refresh') || canCapability('risk_command_center', 'read');
  const canAi = canCapability('risk_command_center', 'ai_analysis');
  const canExport = canCapability('risk_command_center', 'export');

  async function load(refresh = false) {
    if (!canCapability('risk_command_center', 'read')) return;
    setLoading(true); setError(null);
    try {
      const data = await apiFetch<RiskPayload>(`/risk-command-center/summary?lookback=${encodeURIComponent(lookback)}${refresh ? '&refresh=true' : ''}`);
      setPayload(data);
      setSelected(data.tiles[0] ?? null);
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(false); }, [lookback]);

  async function runAi() {
    if (!payload) return;
    setAiLoading(true); setError(null);
    try {
      const r = await apiFetch<{ ok: boolean; analysis: string }>('/risk-command-center/ai-analysis', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lookback, payload }) });
      setAiAnalysis(r.analysis);
    } catch (e) { setError(String(e)); }
    finally { setAiLoading(false); }
  }
  async function exportCsv() {
    if (!payload) return;
    const r = await apiFetch<{ ok: boolean; csv: string; filename: string }>('/risk-command-center/export', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lookback, format: 'csv', payload }) });
    downloadText(r.filename || `risk-command-center-${lookback}.csv`, r.csv, 'text/csv');
  }
  async function exportPdfAudited() {
    if (!payload) return;
    await apiFetch('/risk-command-center/export', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lookback, format: 'pdf', payload, aiAnalysis }) });
    await exportPdf(payload, aiAnalysis);
  }

  const tiles = payload?.tiles ?? [];
  const topCritical = useMemo(() => tiles.filter(t => t.riskLevel === 'Critical' || t.riskLevel === 'High').slice(0, 8), [tiles]);

  return <div className="slide-in space-y-5">
    <div className="rounded-2xl border bg-card p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-primary font-bold">MSSP Leadership View</div>
          <h2 className="text-2xl font-bold">Risk Command Center</h2>
          <p className="text-sm text-muted-foreground max-w-4xl mt-1">Customer tiles ranked by KloudSecure Risk Index using incidents, exposure, secure score, EDR health, connector health, TI hits, SLA breach risk, risky users/devices and latest log ingestion.</p>
          {payload && <div className="text-xs text-muted-foreground mt-2">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'cache hit' : 'fresh scan'}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)} className="rounded-xl border bg-background px-3 py-2 text-sm">{LOOKBACKS.map((x) => <option key={x} value={x}>Lookback {x}</option>)}</select>
          {canRefresh && <button disabled={loading} onClick={() => load(true)} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 font-bold disabled:opacity-50">{loading ? 'Refreshing...' : 'Refresh'}</button>}
          {canAi && <button disabled={!payload || aiLoading} onClick={runAi} className="rounded-xl border px-4 py-2 font-bold disabled:opacity-50">{aiLoading ? 'Analyzing...' : 'AI Analysis'}</button>}
          {canExport && <button disabled={!payload} onClick={exportCsv} className="rounded-xl border px-4 py-2 font-bold disabled:opacity-50">Export CSV</button>}
          {canExport && <button disabled={!payload} onClick={exportPdfAudited} className="rounded-xl border px-4 py-2 font-bold disabled:opacity-50">Export PDF</button>}
        </div>
      </div>
      {error && <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-200">{error}</div>}
    </div>

    {payload && <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-8 gap-3">
      <Metric label="Customers" value={payload.totals.customers} />
      <Metric label="Critical" value={payload.totals.critical} />
      <Metric label="High" value={payload.totals.high} />
      <Metric label="Medium" value={payload.totals.medium} />
      <Metric label="Low" value={payload.totals.low} />
      <Metric label="Open Incidents" value={payload.totals.totalIncidents} />
      <Metric label="Critical Unresolved" value={payload.totals.criticalUnresolved} />
      <Metric label="TI Hits" value={payload.totals.tiHits} />
    </div>}

    {aiAnalysis && <div className="rounded-2xl border bg-card p-5"><h3 className="text-xl font-bold mb-2">AI Analysis</h3><div className="whitespace-pre-wrap text-sm text-muted-foreground">{aiAnalysis}</div></div>}

    {payload && <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-5">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
          {tiles.map((t) => <button key={t.customerId} onClick={() => setSelected(t)} className={`text-left rounded-2xl border bg-card p-4 hover:bg-muted/40 ${selected?.customerId === t.customerId ? 'ring-2 ring-primary' : ''}`}>
            <div className="flex items-start justify-between gap-3"><div><h3 className="font-bold text-lg">{t.customerName}</h3><p className="text-xs text-muted-foreground line-clamp-2">{t.reason}</p></div><div className={`px-2 py-1 rounded-lg border text-xs font-bold ${tone(t.riskIndex)}`}>{t.riskLevel}</div></div>
            <div className="mt-4 flex items-end justify-between"><div><div className="text-xs text-muted-foreground">Risk Index</div><div className="text-4xl font-black">{t.riskIndex}</div></div><div className="text-right text-xs text-muted-foreground">Last ingestion<br /><span className="font-semibold text-foreground">{t.lastSuccessfulLogIngestion ? fmtTs(t.lastSuccessfulLogIngestion) : '-'}</span></div></div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg border p-2"><div className="text-muted-foreground">Incidents</div><div className="font-bold">{t.currentIncidentCount}</div></div>
              <div className="rounded-lg border p-2"><div className="text-muted-foreground">Critical</div><div className="font-bold">{t.criticalUnresolvedCount}</div></div>
              <div className="rounded-lg border p-2"><div className="text-muted-foreground">Exposure</div><div className="font-bold">{t.exposureScore ?? '-'}</div></div>
              <div className="rounded-lg border p-2"><div className="text-muted-foreground">Secure</div><div className="font-bold">{t.secureScore != null ? `${t.secureScore}%` : '-'}</div></div>
            </div>
          </button>)}
        </div>
        {!tiles.length && !loading && <div className="rounded-2xl border bg-card p-5 text-muted-foreground">No accessible customers were returned for the Risk Command Center.</div>}
      </div>
      <div className="space-y-4">
        <div className="rounded-2xl border bg-card p-5"><h3 className="font-bold text-lg mb-3">Focus Today</h3><div className="space-y-2">{(payload.focus ?? []).map((f, i) => <div key={f.customerId} className="rounded-xl border p-3"><div className="flex justify-between gap-2"><div className="font-semibold">{i + 1}. {f.customerName}</div><span className={`px-2 py-1 rounded-lg border text-xs ${tone(f.riskIndex)}`}>{f.riskIndex} {f.riskLevel}</span></div><div className="text-sm text-muted-foreground mt-1">{f.reason}</div></div>)}</div></div>
        {selected && <div className="rounded-2xl border bg-card p-5 space-y-3"><h3 className="font-bold text-lg">{selected.customerName}</h3><div className="grid grid-cols-2 gap-2 text-sm"><Metric label="EDR Health" value={selected.edrHealth} /><Metric label="Connector" value={selected.connectorHealth} /><Metric label="TI Hits" value={selected.tiHitCount} /><Metric label="SLA Risk" value={`${selected.slaBreachRisk}/100`} /><Metric label="Risky Users" value={selected.riskyUsers} /><Metric label="Risky Devices" value={selected.riskyDevices} /></div><button className="rounded-xl border px-4 py-2 text-sm font-bold" onClick={() => setActiveCustomerId(selected.customerId)}>Switch to customer</button><pre className="rounded-xl border bg-muted/30 p-3 text-xs overflow-auto max-h-[260px]">{JSON.stringify(selected.signals, null, 2)}</pre></div>}
        {!!payload.diagnostics.length && <div className="rounded-2xl border bg-card p-5"><h3 className="font-bold mb-2">Diagnostics</h3><ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">{payload.diagnostics.slice(0, 12).map((d) => <li key={d}>{d}</li>)}</ul></div>}
      </div>
    </div>}
  </div>;
}
